import { useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { useLevelsData } from '../../services/levelsService';
import LevelCard from './LevelCard';
import LevelDetailModal from './LevelDetailModal';
import SubscriptionModal from '../../features/dashboard/modals/SubscriptionModal';

const TYPE_MAP = {
    aeroplane: 'Avion',
    helicopter: 'Helicoptere',
    glider: 'Planeur',
    dispatch: 'Dispatch',
};

const TYPE_CONFIG = {
    aeroplane: { labelFr: 'Avion', labelEn: 'Airplane', icon: 'subject-aeroplane' },
    helicopter: { labelFr: 'Hélicoptère', labelEn: 'Helicopter', icon: 'subject-helicopter' },
    glider: { labelFr: 'Planeur', labelEn: 'Glider', icon: 'subject-glider' },
    dispatch: { labelFr: 'Dispatch', labelEn: 'Dispatch', icon: 'subject-dispatch' },
};

const TYPE_ORDER = ['aeroplane', 'helicopter', 'glider', 'dispatch'];

const LevelsModal = ({ onClose }) => {
    const { language } = useLanguage();
    const { levelsData, loading } = useLevelsData();

    const [selectedType, setSelectedType] = useState('aeroplane');
    const [selectedLevel, setSelectedLevel] = useState(null);

    const allLevels = levelsData?.levels || [];

    const uniqueTypes = Array.from(
        new Set(
            allLevels.flatMap(l => {
                const types = [];
                (l.application || '').split(' ').forEach(t => {
                    const internal = Object.entries(TYPE_MAP).find(([, v]) => v === t);
                    if (internal) types.push(internal[0]);
                });
                return types;
            })
        )
    ).sort((a, b) => TYPE_ORDER.indexOf(a) - TYPE_ORDER.indexOf(b));

    const appType = TYPE_MAP[selectedType];
    const filteredLevels = allLevels.filter(
        l => (l.application || '').includes(appType) && l.idNiveau !== 0
    );

    return (
        <div className="fixed inset-0 z-[90] flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="relative bg-gray-50 dark:bg-gray-950 rounded-3xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col border border-gray-200 dark:border-white/10 overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-6 md:px-8 py-5 border-b border-gray-200 dark:border-white/10 bg-white dark:bg-[#121212]">
                    <div>
                        <h2 className="text-xl font-extrabold text-gray-900 dark:text-white">
                            {language === 'fr' ? 'Licences & qualifications' : 'Licences & Ratings'}
                        </h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                            {language === 'fr'
                                ? 'Choisissez votre formation et sa durée.'
                                : 'Choose your training and its duration.'}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-9 h-9 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/10 transition"
                    >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Body (scrollable) */}
                <div className="flex-1 overflow-y-auto custom-scrollbar">
                    {/* Type selector */}
                    {uniqueTypes.length > 1 && (
                        <div className="max-w-xl mx-auto px-6 md:px-8 pt-6 pb-2">
                            <div className="flex bg-gray-200 dark:bg-white/5 rounded-2xl p-1">
                                {uniqueTypes.map(typeId => {
                                    const config = TYPE_CONFIG[typeId];
                                    const isActive = selectedType === typeId;
                                    const label = language === 'fr' ? config.labelFr : config.labelEn;

                                    return (
                                        <button
                                            key={typeId}
                                            onClick={() => setSelectedType(typeId)}
                                            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                                                isActive
                                                    ? 'bg-white dark:bg-white/10 text-gray-900 dark:text-white shadow-sm'
                                                    : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'
                                            }`}
                                        >
                                            <img
                                                src={`/img/subjects/${config.icon}.png`}
                                                alt={label}
                                                className={`w-5 h-5 dark:invert brightness-0 dark:brightness-100 ${isActive ? 'opacity-70 dark:opacity-90' : 'opacity-30'}`}
                                            />
                                            <span className="hidden sm:inline">{label}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Level cards grid */}
                    <div className="px-6 md:px-8 py-6">
                        {loading ? (
                            <div className="text-center py-12 text-gray-500">
                                {language === 'fr' ? 'Chargement...' : 'Loading...'}
                            </div>
                        ) : filteredLevels.length > 0 ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                                {filteredLevels.map(level => (
                                    <LevelCard
                                        key={level.idNiveau}
                                        level={level}
                                        onClick={() => setSelectedLevel(level)}
                                    />
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-12">
                                <p className="text-gray-500 dark:text-gray-400">
                                    {language === 'fr'
                                        ? 'Aucun niveau disponible pour cette catégorie.'
                                        : 'No levels available for this category.'}
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Detail modal for free levels, Subscription modal for paid levels */}
            {selectedLevel && (
                selectedLevel.isFree ? (
                    <LevelDetailModal
                        level={selectedLevel}
                        allLevels={allLevels}
                        onClose={() => setSelectedLevel(null)}
                    />
                ) : (
                    <SubscriptionModal
                        levelVar={selectedLevel.var}
                        onClose={() => setSelectedLevel(null)}
                    />
                )
            )}
        </div>
    );
};

export default LevelsModal;
